import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Globe,
  Plus,
  Trash2,
  Pencil,
  ToggleLeft,
  ToggleRight,
  X,
  Loader2,
} from 'lucide-react';
import {
  bitrixAllowedDomainService,
  BitrixAllowedDomain,
  UpdateBitrixAllowedDomainDto,
} from '@/services/bitrixAllowedDomainService';

export default function BitrixAllowedDomains() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [domain, setDomain] = useState('');
  const [clientName, setClientName] = useState('');
  const [notes, setNotes] = useState('');

  const [editing, setEditing] = useState<BitrixAllowedDomain | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BitrixAllowedDomain | null>(null);

  const { data: domains, isLoading, error } = useQuery({
    queryKey: ['bitrixAllowedDomains'],
    queryFn: () => bitrixAllowedDomainService.getAll(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bitrixAllowedDomains'] });

  const createMutation = useMutation({
    mutationFn: () =>
      bitrixAllowedDomainService.create({
        domain: domain.trim(),
        clientName: clientName.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(t('bitrixDomainCreated') || 'Dominio registrado correctamente');
      setDomain('');
      setClientName('');
      setNotes('');
      invalidate();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || t('bitrixDomainCreateError') || 'No se pudo registrar el dominio',
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: UpdateBitrixAllowedDomainDto }) =>
      bitrixAllowedDomainService.update(payload.id, payload.data),
    onSuccess: () => {
      toast.success(t('bitrixDomainUpdated') || 'Dominio actualizado');
      setEditing(null);
      invalidate();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || t('bitrixDomainUpdateError') || 'No se pudo actualizar el dominio',
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bitrixAllowedDomainService.delete(id),
    onSuccess: () => {
      toast.success(t('bitrixDomainDeleted') || 'Dominio eliminado');
      setDeleteTarget(null);
      invalidate();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message || t('bitrixDomainDeleteError') || 'No se pudo eliminar el dominio',
      );
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    createMutation.mutate();
  };

  const handleToggleEnabled = (item: BitrixAllowedDomain) => {
    updateMutation.mutate({ id: item.id, data: { isEnabled: !item.isEnabled } });
  };

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        <Globe className="h-6 w-6 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('bitrixAllowedDomains') || 'Dominios Bitrix24 permitidos'}
          </h1>
          <p className="text-sm text-gray-600">
            {t('bitrixAllowedDomainsDescription') ||
              'Pre-registrá el dominio de cada cliente antes de que instale la app en su Bitrix24. Solo los dominios habilitados aquí pueden completar la instalación.'}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleCreate}
        className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:grid-cols-2 lg:grid-cols-4"
      >
        <div className="lg:col-span-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('domain') || 'Dominio'}
          </label>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="bitrix24.cliente.com"
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="lg:col-span-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('clientName') || 'Cliente'}
          </label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder={t('optional') || 'Opcional'}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="lg:col-span-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t('notes') || 'Notas'}
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('optional') || 'Opcional'}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-end lg:col-span-1">
          <button
            type="submit"
            disabled={createMutation.isPending || !domain.trim()}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {t('register') || 'Registrar'}
          </button>
        </div>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-600">{t('loading') || 'Cargando...'}</p>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-red-500">
            {t('bitrixAllowedDomainsLoadError') || 'Error al cargar los dominios'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('domain') || 'Dominio'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('clientName') || 'Cliente'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('notes') || 'Notas'}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('status') || 'Estado'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t('actions') || 'Acciones'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {domains?.map((item) => (
                  <tr key={item.id} className="transition hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      <code className="rounded bg-gray-100 px-2 py-1 text-xs">{item.domain}</code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                      {item.clientName || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-600">
                      {item.notes || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleEnabled(item)}
                        disabled={updateMutation.isPending}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          item.isEnabled
                            ? 'bg-green-600 hover:bg-red-600'
                            : 'bg-gray-400 hover:bg-green-600'
                        }`}
                        title={
                          item.isEnabled
                            ? t('clickToDisable') || 'Click para deshabilitar'
                            : t('clickToEnable') || 'Click para habilitar'
                        }
                      >
                        {item.isEnabled ? (
                          <ToggleRight className="h-4 w-4" />
                        ) : (
                          <ToggleLeft className="h-4 w-4" />
                        )}
                        {item.isEnabled ? t('enabled') || 'Habilitado' : t('disabled') || 'Deshabilitado'}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditing(item)}
                          className="inline-flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-200"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {t('edit') || 'Editar'}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('delete') || 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {domains?.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-gray-500">
                {t('noBitrixAllowedDomains') || 'Todavía no registraste ningún dominio.'}
              </p>
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditDomainModal
          item={editing}
          isPending={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSave={(data) => updateMutation.mutate({ id: editing.id, data })}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-lg">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('areYouSure') || '¿Estás seguro?'}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('confirmDeleteBitrixDomain', { domain: deleteTarget.domain }) ||
                  `Vas a eliminar "${deleteTarget.domain}" de los dominios permitidos. Un cliente con este dominio ya no podrá instalar la app.`}
              </p>
            </div>
            <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('cancel') || 'Cancelar'}
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteMutation.isPending ? t('processing') || 'Procesando...' : t('delete') || 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EditDomainModal({
  item,
  isPending,
  onClose,
  onSave,
}: {
  item: BitrixAllowedDomain;
  isPending: boolean;
  onClose: () => void;
  onSave: (data: UpdateBitrixAllowedDomainDto) => void;
}) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState(item.domain);
  const [clientName, setClientName] = useState(item.clientName || '');
  const [notes, setNotes] = useState(item.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      domain: domain.trim(),
      clientName: clientName.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('editBitrixDomain') || 'Editar dominio'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 px-6 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('domain') || 'Dominio'}
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('clientName') || 'Cliente'}
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('notes') || 'Notas'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('cancel') || 'Cancelar'}
          </button>
          <button
            type="submit"
            disabled={isPending || !domain.trim()}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPending ? t('processing') || 'Procesando...' : t('save') || 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
